// Builds the STARCODE morph target: samples random points across the *surface* of a real
// extruded, beveled 3D TextGeometry (front face, back face, bevel and side walls all included),
// weighted by triangle area for even coverage. Each particle gets exactly one target point, so
// when the morph engages every single particle in the swarm has its own designated spot
// somewhere on the lettering - giving proper volumetric 3D typography instead of a flat sprite.

import * as THREE from "three";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";

// Curated high-end typography fonts for graphic designers:
// Modern Swiss Grotesk, Geometric Sans, Futuristic Tech, Classic High-End Serif, Contemporary Mono
export const DESIGNER_FONTS = [
  { name: "Helvetiker Bold (Modern Swiss Clean)", url: "/fonts/helvetiker_bold.typeface.json", size: 130, depth: 34, targetWidth: 520 },
  { name: "Helvetiker Regular (Neo-Grotesk Minimal)", url: "/fonts/helvetiker_regular.typeface.json", size: 130, depth: 30, targetWidth: 520 },
  { name: "Optimer Bold (High-End Geometric)", url: "/fonts/optimer_bold.typeface.json", size: 130, depth: 34, targetWidth: 530 },
  { name: "Optimer Regular (Refined Editorial)", url: "/fonts/optimer_regular.typeface.json", size: 130, depth: 30, targetWidth: 530 },
  { name: "Gentilis Bold (Futuristic Dynamic)", url: "/fonts/gentilis_bold.typeface.json", size: 130, depth: 34, targetWidth: 520 },
  { name: "Gentilis Regular (Sleek Tech Sans)", url: "/fonts/gentilis_regular.typeface.json", size: 130, depth: 30, targetWidth: 520 },
  { name: "Droid Serif Bold (Classic Editorial Serif)", url: "/fonts/droid_serif_bold.typeface.json", size: 130, depth: 36, targetWidth: 540 },
  { name: "Droid Serif Regular (Timeless Book Serif)", url: "/fonts/droid_serif_regular.typeface.json", size: 130, depth: 32, targetWidth: 540 },
  { name: "Droid Sans Bold (Contemporary Grotesque)", url: "/fonts/droid_sans_bold.typeface.json", size: 130, depth: 34, targetWidth: 520 },
  { name: "Droid Sans Mono (Cyberpunk Brutalist)", url: "/fonts/droid_sans_mono_regular.typeface.json", size: 125, depth: 32, targetWidth: 550 },
];

export async function buildTextTargets(word, count, options = {}) {
  const chosenFont = options.fontUrl
    ? { url: options.fontUrl, size: options.size ?? 130, depth: options.depth ?? 34, targetWidth: options.targetWidth ?? 520 }
    : DESIGNER_FONTS[Math.floor(Math.random() * DESIGNER_FONTS.length)];

  const fontUrl = options.fontUrl || chosenFont.url;
  const size = options.size ?? chosenFont.size ?? 130;
  const depth = options.depth ?? chosenFont.depth ?? 34;
  const targetWidth = options.targetWidth ?? chosenFont.targetWidth ?? 520;
  const offsetY = options.offsetY ?? 60;

  const loader = new FontLoader();
  let font;
  try {
    font = await new Promise((resolve, reject) => loader.load(fontUrl, resolve, undefined, reject));
  } catch (err) {
    console.warn(`[STARCODE] Font ${fontUrl} failed to load, fallback to default:`, err);
    font = await new Promise((resolve, reject) => loader.load("/fonts/helvetiker_bold.typeface.json", resolve, undefined, reject));
  }

  const geometry = new TextGeometry(word, {
    font,
    size,
    depth,
    curveSegments: 6,
    bevelEnabled: true,
    bevelThickness: 3.2,
    bevelSize: 2.1,
    bevelSegments: 3,
  });
  geometry.computeBoundingBox();
  geometry.computeVertexNormals();

  const bbox = geometry.boundingBox;
  const center = new THREE.Vector3();
  bbox.getCenter(center);
  geometry.translate(-center.x, -center.y, -center.z);

  const nonIndexed = geometry.index ? geometry.toNonIndexed() : geometry;
  const posAttr = nonIndexed.attributes.position;
  const triCount = posAttr.count / 3;

  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  const cross = new THREE.Vector3();

  const triAreas = new Float32Array(triCount);
  let totalArea = 0;
  for (let i = 0; i < triCount; i++) {
    a.fromBufferAttribute(posAttr, i * 3 + 0);
    b.fromBufferAttribute(posAttr, i * 3 + 1);
    c.fromBufferAttribute(posAttr, i * 3 + 2);
    ab.subVectors(b, a);
    ac.subVectors(c, a);
    cross.crossVectors(ab, ac);
    const area = cross.length() * 0.5;
    triAreas[i] = area;
    totalArea += area;
  }

  // Cumulative distribution for area-weighted triangle picking (binary search per sample).
  const cumulative = new Float32Array(triCount);
  let running = 0;
  for (let i = 0; i < triCount; i++) {
    running += triAreas[i];
    cumulative[i] = running;
  }

  function pickTriangleIndex() {
    const r = Math.random() * totalArea;
    let lo = 0;
    let hi = triCount - 1;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (cumulative[mid] < r) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  // Fit the whole word into a reasonable world-space width regardless of font metrics/word length.
  geometry.computeBoundingBox();
  const size2 = new THREE.Vector3();
  geometry.boundingBox.getSize(size2);
  const scale = size2.x > 0 ? targetWidth / size2.x : 1;

  const targets = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) {
    const triIndex = pickTriangleIndex();
    a.fromBufferAttribute(posAttr, triIndex * 3 + 0);
    b.fromBufferAttribute(posAttr, triIndex * 3 + 1);
    c.fromBufferAttribute(posAttr, triIndex * 3 + 2);

    // Uniform random barycentric point on the triangle.
    let u = Math.random();
    let v = Math.random();
    if (u + v > 1) {
      u = 1 - u;
      v = 1 - v;
    }
    const px = a.x + u * (b.x - a.x) + v * (c.x - a.x);
    const py = a.y + u * (b.y - a.y) + v * (c.y - a.y);
    const pz = a.z + u * (b.z - a.z) + v * (c.z - a.z);

    targets[i * 4 + 0] = px * scale;
    targets[i * 4 + 1] = py * scale + offsetY;
    targets[i * 4 + 2] = pz * scale;
    targets[i * 4 + 3] = 1.0;
  }

  return targets;
}
